import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { AsyncPipe } from '@angular/common';
import { Component, signal, inject, Injector } from '@angular/core';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import {
  OnyxAutoFocusDirective,
  OnyxButtonComponent,
  OnyxIconComponent,
  OnyxMessageComponent,
  OnyxModalComponent,
  OnyxTabsComponent,
  OnyxTextFieldComponent,
  OnyxToastService,
} from '@onyx/angular';
import { Subject } from 'rxjs';
import { DictionaryCode } from '../../../../../../common/enums/dictionary-code';
import { ValidationHelper } from '../../../../../../common/helpers/validation.helper';
import { DictionariesService } from '../../../../../../common/services/dictionaries.service';
import type { Contractor } from '../../interfaces/contractor';
import { ContractorsService } from '../../services/contractors.service';

export type BlockContractorModalForm = ReturnType<
  ReturnType<BlockContractorModalComponent['buildForm']>['getRawValue']
>;

@Component({
  selector: 'app-block-contractor-modal',
  imports: [
    OnyxModalComponent,
    OnyxMessageComponent,
    OnyxIconComponent,
    TranslatePipe,
    ReactiveFormsModule,
    OnyxButtonComponent,
    OnyxTextFieldComponent,
    OnyxTabsComponent,
    AsyncPipe,
    OnyxAutoFocusDirective,
  ],
  templateUrl: './block-contractor-modal.component.html',
  styleUrl: './block-contractor-modal.component.scss',
})
export class BlockContractorModalComponent {
  protected readonly contractor = inject<Contractor>(DIALOG_DATA);
  protected readonly dialogRef = inject(DialogRef);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly dictionariesService = inject(DictionariesService);
  private readonly contractorsService = inject(ContractorsService);
  private readonly toastService = inject(OnyxToastService);
  private readonly injector = inject(Injector);

  protected readonly I18N = 'contractors.blockContractorModal';

  protected readonly contractorBlockReason$ =
    this.dictionariesService.getDictionary(
      DictionaryCode.CONTRACTOR_BLOCK_REASON,
    );

  protected form = this.buildForm();
  protected loading = signal(false);
  protected close$ = new Subject<boolean>();

  protected async submit(): Promise<void> {
    if (!(await ValidationHelper.checkValidity(this.form, this.injector))) {
      return;
    }

    const form = this.form.getRawValue();
    this.loading.set(true);
    this.contractorsService
      .blockContractor(this.contractor.uuid, form)
      .subscribe(() => {
        this.toastService.showSuccess({
          message: 'contractors.toasts.contractorBlocked',
        });
        this.close$.next(true);
      })
      .add(() => this.loading.set(false));
  }

  private buildForm() {
    return this.fb.group({
      reason: this.fb.control('late-payments', [Validators.required]),
      reasonDescription: this.fb.control('', [Validators.required]),
      end: this.fb.group({
        date: this.fb.control(null),
        indefinite: this.fb.control(true),
      }),
    });
  }
}
